import { call, put, all, takeEvery, takeLatest, select } from "redux-saga/effects";
import { generalActions, rosteringActions } from "../actions";
import { fetchShiftRequests } from "../actions/rostering.actions";
import api from "../services/rostering.services";
import ACTIONS from "../constants";
import { TOAST_TYPE, PAYROLL_EXPORT_STATUS } from "Constants/constant";
import { parseBooleanVal } from "Utils/utils";
import { normalizeAdvanceShiftCreateResponse } from "../../pages/Rostering/WorkerShift/components/advanceModeSplitDefaults";
import {
  buildShiftRequestScheduleAuditChanges,
  buildShiftRequestSyncParamsFromPayload,
  shiftRequestPayloadHasAssignedWorker,
  resolveShiftRequestWorkerFromPayload,
} from "Utils/shiftRequestScheduleAudit";

/** Recurring create summary from V2 (`recurring` sibling or `data.recurring`) or V5 advance. */
const getShiftCreateRecurringResult = (response) => {
  if (!response || typeof response !== "object") return null;
  return (
    response.recurring ||
    response.advance_shift?.recurring ||
    response.data?.advance_shift?.recurring ||
    response.data?.recurring ||
    null
  );
};

const isShiftLikeCreateItem = (item) =>
  Boolean(
    item &&
      typeof item === "object" &&
      !Array.isArray(item) &&
      (item.uuid || item.id) &&
      item.success === undefined
  );

/** V2 create used to mix `{0: shift, recurring}` which is not a JSON array. */
const normalizeV2ShiftCreateData = (data) => {
  if (!data) return [];
  if (Array.isArray(data.shifts)) {
    return data.shifts.filter(isShiftLikeCreateItem);
  }
  if (Array.isArray(data)) {
    return data.filter(isShiftLikeCreateItem);
  }
  if (typeof data !== "object") return [];
  if (isShiftLikeCreateItem(data)) {
    return [data];
  }
  return Object.entries(data)
    .filter(
      ([key]) =>
        key !== "recurring" && key !== "overlapped" && key !== "shifts"
    )
    .map(([, value]) => value)
    .filter(isShiftLikeCreateItem);
};

export default function* rootSaga() {
  function* getWorkerAndParticipantList(state) {
    try {
      const pendingPayload = {
        calendarView: state.calendarView,
        search: state.payload.hasOwnProperty("search")
          ? state.payload.search
          : null,
      };
      yield put(
        rosteringActions.workerAndParticipantListfetched.pending(pendingPayload)
      );
      const response = yield call(
        api.getWorkerAndParticipantList,
        state.payload
      );
      if (response) {
        yield put(
          rosteringActions.workerAndParticipantListfetched.success({
            data: response,
            isRefreshCall: state.isRefreshCall || false,
          })
        );
      }
    } catch (error) {
      yield put(
        rosteringActions.workerAndParticipantListfetched.error(
          error.message ?? error
        )
      );
    }
  }
  function* createOrUpdateWorkerShift(state) {
    try {
      yield put(rosteringActions.workerShiftCreatedOrUpdated.pending);
      const useAdvanceCreate =
        state.isAdvanceShift && !state.isUpdate;
      const useAdvanceUpdate =
        state.isAdvanceShift && state.isUpdate;
      const useAdvance = useAdvanceCreate || useAdvanceUpdate;
      const response = yield call(
        useAdvanceUpdate
          ? api.updateAdvanceWorkerShift
          : state.isUpdate
          ? api.updateWorkerShift
          : useAdvanceCreate
          ? api.createAdvanceWorkerShift
          : api.createWorkerShift,
        state.payload
      );

      if (
        response &&
        (response.data || response.advance_shift || useAdvance)
      ) {
        let responseData;
        if (useAdvance) {
          const normalized = normalizeAdvanceShiftCreateResponse(response);
          responseData = normalized ? [normalized] : [];
        } else {
          responseData = normalizeV2ShiftCreateData(response.data);
        }

        const recurringResult = getShiftCreateRecurringResult(response);

        const pendingShiftRequestUuid = yield select(
          (s) => s.rostering?.get?.("pendingShiftRequestApprovalUuid") || null
        );

        const shiftRequestLeaveMessage =
          "This worker is on leave during the selected time.";

        const wasPublished =
          state.payload?.is_publish === true ||
          state.payload?.is_publish === 1 ||
          response?.advance_shift?.published === true ||
          response?.data?.advance_shift?.published === true;

        let shiftRequestApproved = false;
        let shiftRequestInReview = false;
        let shiftRequestApproveFailed = false;

        // From Shift Requests "View":
        // worker assigned → approved (Create, or Create and Publish).
        // no worker → in_review. Request must not stay pending once the shift exists.
        // Recurring still creates the anchor shift in responseData[0] — close the request too.
        if (pendingShiftRequestUuid) {
          const workerId = resolveShiftRequestWorkerFromPayload(state.payload);
          const linkedShiftUuid =
            responseData?.[0]?.uuid ||
            state.payload?.uuid ||
            null;
          let scheduleChanges = {};
          try {
            const detailRes = yield call(
              api.getShiftRequestDetail,
              pendingShiftRequestUuid
            );
            const requestRow =
              detailRes?.data?.data ?? detailRes?.data ?? detailRes ?? {};
            scheduleChanges = buildShiftRequestScheduleAuditChanges(
              requestRow,
              state.payload
            );
          } catch (scheduleAuditError) {
            scheduleChanges = {};
          }
          const scheduleSyncParams =
            buildShiftRequestSyncParamsFromPayload(state.payload);
          const hasAssignedWorker =
            shiftRequestPayloadHasAssignedWorker(state.payload);
          const workerIdForStatusApi = workerId;
          const shiftAuditParams = {
            ...(hasAssignedWorker && workerIdForStatusApi != null
              ? { worker_id: workerIdForStatusApi }
              : {}),
            ...(linkedShiftUuid ? { shift_uuid: linkedShiftUuid } : {}),
            ...(Object.keys(scheduleChanges).length > 0
              ? { schedule_changes: JSON.stringify(scheduleChanges) }
              : {}),
            ...scheduleSyncParams,
          };
          const createdShiftStatus = String(
            responseData?.[0]?.status || ""
          ).toLowerCase();
          const shiftCountsAsScheduled =
            wasPublished ||
            createdShiftStatus === "accepted" ||
            createdShiftStatus === "published";
          const shouldCloseRequest = !state.isUpdate || wasPublished;
          if (shouldCloseRequest) {
            const targetStatus = hasAssignedWorker ? "approved" : "in_review";
            try {
              yield call(
                api.updateShiftRequestStatus,
                pendingShiftRequestUuid,
                targetStatus,
                {
                  ...shiftAuditParams,
                  shift_published: shiftCountsAsScheduled ? 1 : 0,
                }
              );
              yield put(fetchShiftRequests());
              if (targetStatus === "approved") {
                shiftRequestApproved = true;
              } else {
                shiftRequestInReview = true;
              }
              yield put({
                type: ACTIONS.CLEAR_PENDING_SHIFT_REQUEST_APPROVAL,
              });
            } catch (approveError) {
              const approveMessage = approveError?.message ?? "";
              const isLeaveConflict =
                approveMessage.includes("on leave during the selected time");

              if (isLeaveConflict) {
                shiftRequestApproveFailed = true;
                yield put(
                  rosteringActions.workerShiftCreatedOrUpdated.error(
                    shiftRequestLeaveMessage
                  )
                );
              }

              yield put(
                generalActions.showToast({
                  type: TOAST_TYPE.ERROR,
                  title: isLeaveConflict
                    ? shiftRequestLeaveMessage
                    : approveMessage ||
                      "Shift saved but failed to update shift request status",
                })
              );

              if (!isLeaveConflict) {
                yield put({
                  type: ACTIONS.CLEAR_PENDING_SHIFT_REQUEST_APPROVAL,
                });
              }
            }

            if (shiftRequestApproveFailed) {
              return;
            }
          }
        }

        yield put(
          rosteringActions.workerShiftCreatedOrUpdated.success({
            response: responseData,
            calendarView: state.calendarView,
            isUpdate: state.isUpdate,
            isWorkerChange: state.isWorkerChange,
            prevWorkerUUID: state.prevWorkerUUID,
            recurring: recurringResult,
            previousShiftUuid:
              state.isUpdate && state.payload?.uuid
                ? state.payload.uuid
                : undefined,
          })
        );

        // Recurring summary modal after success so its payload is in the store.
        // Do not refresh the calendar here — that delayed the modal until after
        // the create dialog closed and the roster reloaded. Done on the modal
        // calls refreshCalendar (same as basic recurring create).
        if (recurringResult) {
          yield put(rosteringActions.recurringMessageModal(true));
          if (shiftRequestApproved) {
            yield put(
              generalActions.showToast({
                type: TOAST_TYPE.SUCCESS,
                title: "Recurring shift created and request approved",
              })
            );
          } else if (shiftRequestInReview) {
            yield put(
              generalActions.showToast({
                type: TOAST_TYPE.SUCCESS,
                title:
                  "Recurring shift created. Assign a worker from the In Review tab to approve the request.",
              })
            );
          }
        } else if (shiftRequestApproved) {
          yield put(
            generalActions.showToast({
              type: TOAST_TYPE.SUCCESS,
              title: "Shift scheduled and request approved",
            })
          );
        } else if (shiftRequestInReview) {
          yield put(
            generalActions.showToast({
              type: TOAST_TYPE.SUCCESS,
              title: wasPublished
                ? "Shift published. Assign a worker from the In Review tab to approve the request."
                : "Shift created. Assign a worker from the In Review tab to approve the request.",
            })
          );
        } else {
          const publishSuffix = wasPublished ? " and Published" : "";
          yield put(
            generalActions.showToast({
              type: TOAST_TYPE.SUCCESS,
              title: `Shift ${
                state.isUpdate ? "Updated" : "Created"
              }${publishSuffix} Successfully!`,
            })
          );
        }

        if (state?.callback) {
          try {
            state.callback();
          } catch (callbackError) {
            // Create/update already succeeded. Stats refresh must not surface
            // as "Something Went Wrong!" (e.g. Add Shift toolbar omits the fn).
          }
        }
      }
    } catch (error) {
      yield put(
        generalActions.showToast({
          type: TOAST_TYPE.ERROR,
          title: error?.message
            ?.toLowerCase()
            ?.includes("same time already exists")
            ? `${state.name} - ${error?.message}`
            : error?.message
            ?.toLowerCase()
            ?.includes("has leave on") ?   `${error.message}` : "Something Went Wrong!",
        })
      );
      yield put(
        rosteringActions.workerShiftCreatedOrUpdated.error(
          error.message ?? error
        )
      );
    }
  }
  // function* createOrUpdateWorkerShift(state) {
  //   try {
  //     yield put(rosteringActions.workerShiftCreatedOrUpdated.pending);
  //     const response = yield call(
  //       state.isUpdate ? api.updateWorkerShift : api.createWorkerShift,
  //       state.payload
  //     );
  //     if (response && response.data) {

  //       // yield put(
  //       //   generalActions.showToast({
  //       //     type: TOAST_TYPE.SUCCESS,
  //       //     title: `Shift ${
  //       //       state.isUpdate ? "Updated" : "Created"
  //       //     } Successfully!`,
  //       //   })
  //       // );

  //       yield put(
  //         rosteringActions.workerShiftCreatedOrUpdated.success({
  //           // response: state.isUpdate ? response?.data?.shift : response?.data,
  //           // response: {
  //           //   ...response?.data || [],
  //           //   // worker_id: response.data[0].worker?.id,
  //           //   // pt_id: response.data[0].participants[0]?.id,
  //           // },
  //           response: state.isUpdate ? [response.data] : response.data || [],
  //           calendarView: state.calendarView,
  //           isUpdate: state.isUpdate,
  //           isWorkerChange: state.isWorkerChange,
  //           prevWorkerUUID: state.prevWorkerUUID,
  //           recurring: {
  //             ...response.data.recurring,
  //           },
  //         })
  //       );

  //       if (response.data.hasOwnProperty("recurring")) {
  //         yield put(rosteringActions.recurringMessageModal(true));
  //       } else{
  //         yield put(
  //           generalActions.showToast({
  //             type: TOAST_TYPE.SUCCESS,
  //             title: `Shift ${
  //               state.isUpdate ? "Updated" : "Created"
  //             } Successfully!`,
  //           })
  //         );
  //       }

  //       // if (!response.data.hasOwnProperty("recurring")) {
  //       //   console.log("in recurring cond")
  //       //   yield put(
  //       //     generalActions.showToast({
  //       //       type: TOAST_TYPE.SUCCESS,
  //       //       title: `Shift ${
  //       //         state.isUpdate ? "Updated" : "Created"
  //       //       } Successfully!`,
  //       //     })
  //       //   );
  //       // } else {
  //       //   yield put(rosteringActions.recurringMessageModal(true));
  //       // }
  //     }
  //   } catch (error) {
  //     yield put(
  //       generalActions.showToast({
  //         type: TOAST_TYPE.ERROR,
  //         title: error?.message
  //           ?.toLowerCase()
  //           ?.includes("same time already exists")
  //           ? `${state.name} - ${error?.message}`
  //           : "Something Went Wrong!",
  //       })
  //     );
  //     yield put(
  //       rosteringActions.workerShiftCreatedOrUpdated.error(
  //         error.message ?? error
  //       )
  //     );
  //   }
  // }

  // function* createOrUpdateWorkerShift(state) {
  //   try {
  //     yield put(rosteringActions.workerShiftCreatedOrUpdated.pending);
  //     const response = yield call(
  //       state.isUpdate ? api.updateWorkerShift : api.createWorkerShift,
  //       state.payload
  //     );
  //     if (response && response.data) {

  //       yield put(
  //         generalActions.showToast({
  //           type: TOAST_TYPE.SUCCESS,
  //           title: `Shift ${
  //             state.isUpdate ? "Updated" : "Created"
  //           } Successfully!`,
  //         })
  //       );

  //       yield put(
  //         rosteringActions.workerShiftCreatedOrUpdated.success({
  //           // response: state.isUpdate ? response?.data?.shift : response?.data,
  //           // response: {
  //           //   ...response?.data || [],
  //           //   // worker_id: response.data[0].worker?.id,
  //           //   // pt_id: response.data[0].participants[0]?.id,
  //           // },
  //           response: state.isUpdate ? [response.data] : response.data || [],
  //           calendarView: state.calendarView,
  //           isUpdate: state.isUpdate,
  //           isWorkerChange: state.isWorkerChange,
  //           prevWorkerUUID: state.prevWorkerUUID,
  //           recurring: {
  //             ...response.data.recurring,
  //           },
  //         })
  //       );

  //       // if (response.data.hasOwnProperty("recurring")) {
  //       //   yield put(rosteringActions.recurringMessageModal(true));
  //       // } else{
  //       //   yield put(
  //       //     generalActions.showToast({
  //       //       type: TOAST_TYPE.SUCCESS,
  //       //       title: `Shift ${
  //       //         state.isUpdate ? "Updated" : "Created"
  //       //       } Successfully!`,
  //       //     })
  //       //   );
  //       // }

  //       // if (!response.data.hasOwnProperty("recurring")) {
  //       //   console.log("in recurring cond")
  //       //   yield put(
  //       //     generalActions.showToast({
  //       //       type: TOAST_TYPE.SUCCESS,
  //       //       title: `Shift ${
  //       //         state.isUpdate ? "Updated" : "Created"
  //       //       } Successfully!`,
  //       //     })
  //       //   );
  //       // } else {
  //       //   yield put(rosteringActions.recurringMessageModal(true));
  //       // }
  //     }
  //   } catch (error) {
  //     yield put(
  //       generalActions.showToast({
  //         type: TOAST_TYPE.ERROR,
  //         title: error?.message
  //           ?.toLowerCase()
  //           ?.includes("same time already exists")
  //           ? `${state.name} - ${error?.message}`
  //           : "Something Went Wrong!",
  //       })
  //     );
  //     yield put(
  //       rosteringActions.workerShiftCreatedOrUpdated.error(
  //         error.message ?? error
  //       )
  //     );
  //   }
  // }

  function* copyWorkerAndParticipantShift(state) {
    try {
      yield put(
        rosteringActions.workerAndParticipantShiftCopied.pending({
          data: { ...state.payload.payload, userId: state.payload.userId },
        })
      );

      const response = yield call(
        api.copyWorkerAndParticipantShift,
        state.payload.payload
      );
      if (response && response.data) {
        let responseData = response.data;
        if (parseBooleanVal(responseData?.is_multi_assignment)) {
          const normalized = normalizeAdvanceShiftCreateResponse({
            data: responseData,
          });
          if (normalized) {
            responseData = normalized;
          }
        }

        yield put(
          rosteringActions.workerAndParticipantShiftCopied.success({
            response: responseData,
            calendarView: state.payload.calendarView,
          })
        );
        yield put(
          generalActions.showToast({
            type: TOAST_TYPE.SUCCESS,
            title: `${response?.message}`,
          })
        );
      }
    } catch (error) {
      yield put(
        generalActions.showToast({
          type: TOAST_TYPE.ERROR,
          title: error?.message ?? "Something Went Wrong!",
        })
      );
      yield put(
        rosteringActions.workerAndParticipantShiftCopied.error(
          error.message ?? error
        )
      );
    }
  }

  function* getWorkerTimesheets(state) {
    try {
      yield put(
        rosteringActions.workerTimeSheetsFetched.pending({
          paginationCall: state.payload.paginationCall,
          isRefreshList: state.isRefreshList,
        })
      );
      const response = yield call(api.getWorkerTimeSheets, state.payload);

      if (response && response.data) {
        yield put(
          rosteringActions.workerTimeSheetsFetched.success(response.data)
        );
      }
    } catch (error) {
      yield put(
        rosteringActions.workerTimeSheetsFetched.error(error.message ?? error)
      );
    }
  }

  function* getParticipantListForTimesheet() {
    try {
      yield put(rosteringActions.participantListForTimeSheet.pending);

      const response = yield call(api.getParticipantListForTimesheet);
      if (response && response.data) {
        yield put(
          rosteringActions.participantListForTimeSheet.success(response.data)
        );
      }
    } catch (error) {
      yield put(
        rosteringActions.participantListForTimeSheet.error(
          error.message ?? error
        )
      );
    }
  }

  function* getTimesheetComments(state) {
    try {
      yield put(
        rosteringActions.timeSheetCommentsFetched.pending(
          state.payload.hasOwnProperty("isFirstCall") &&
            parseBooleanVal(state.payload.isFirstCall)
            ? true
            : false
        )
      );
      const response = yield call(api.getTimesheetComments, state.payload);
      if (response && response.data) {
        yield put(
          rosteringActions.timeSheetCommentsFetched.success(response.data)
        );
      }
    } catch (error) {
      yield put(
        rosteringActions.timeSheetCommentsFetched.error(error.message ?? error)
      );
    }
  }

  function* createTimesheetComment(state) {
    try {
      yield put(rosteringActions.timeSheetCommentCreated.pending);
      const response = yield call(api.createTimesheetComment, state.payload);
      if (response && response.data) {
        yield put(
          rosteringActions.timeSheetCommentCreated.success(response.data)
        );
        yield put(
          generalActions.showToast({
            type: TOAST_TYPE.SUCCESS,
            title: "Comment Created Successfully!",
          })
        );
      }
    } catch (error) {
      yield put(
        rosteringActions.timeSheetCommentCreated.error(error.message ?? error)
      );
      yield put(
        generalActions.showToast({
          type: TOAST_TYPE.ERROR,
          title: error?.message ?? "Something Went Wrong!",
        })
      );
    }
  }

  function* updateWorkerTimesheet(state) {
    try {
      yield put(rosteringActions.timeSheetDataUpdated.pending);
      const response = yield call(api.updateTimesheetData, state.payload);
      if (response && response.data) {
        yield put(
          rosteringActions.timeSheetDataUpdated.success({
            data: { ...response.data, comment: state.payload?.data?.comment },
            uuids: state.payload.uuids,
            isNext: state.isNext,
          })
        );
        if (state.callback) {
          state.callback(true);
        }
        yield put(
          generalActions.showToast({
            type: TOAST_TYPE.SUCCESS,
            title: "Timesheet Updated Successfully!",
          })
        );
      }
    } catch (error) {
      yield put(
        rosteringActions.timeSheetDataUpdated.error(error.message ?? error)
      );
      yield put(
        generalActions.showToast({
          type: TOAST_TYPE.ERROR,
          title: error?.message ?? "Something Went Wrong!",
        })
      );
    }
  }

  function* changeTimesheetStatus(state) {
    try {
      yield put(rosteringActions.timeSheetStatusChanged.pending(state.payload));
      const response = yield call(
        api.changeTimesheetStatus,
        state.payload.data
      );
      if (response.message) {
        yield put(
          rosteringActions.timeSheetStatusChanged.success(state.payload)
        );
        yield put(
          generalActions.showToast({
            type: TOAST_TYPE.SUCCESS,
            title: "Timesheet Status Updated Successfully!",
          })
        );
      }
    } catch (error) {
      yield put(
        rosteringActions.timeSheetStatusChanged.error(error.message ?? error)
      );
      yield put(
        generalActions.showToast({
          type: TOAST_TYPE.ERROR,
          title: error?.message ?? "Something Went Wrong!",
        })
      );
    }
  }

  function* getWorkerTimesheetExportList(state) {
    try {
      yield put(rosteringActions.workerTimeSheetExportList.pending);
      const response = yield call(api.workerTimeSheetExportList, state.payload);
      if (response && response.data) {
        yield put(
          rosteringActions.workerTimeSheetExportList.success(response.data)
        );
      }
    } catch (error) {
      yield put(
        rosteringActions.workerTimeSheetExportList.error(error.message ?? error)
      );
    }
  }

  function* getSingleBreakdownTimesheet(state) {
    try {
      yield put(rosteringActions.singleTimesheetDetails.pending);

      const response = yield call(api.singleTimesheetDetails, state.payload);
      const timesheetPayload = response?.data?.data ?? response?.data;
      if (timesheetPayload) {
        yield put(
          rosteringActions.singleTimesheetDetails.success(timesheetPayload)
        );
      }
    } catch (error) {
      yield put(
        rosteringActions.singleTimesheetDetails.error(error.message ?? error)
      );
    }
  }

  function* exportTimesheetToPayroll(state) {
    try {
      yield put(
        rosteringActions.exportTimesheetToPayroll.pending(state.payload.type)
      );
      const response = yield call(api.exportTimesheetToPayroll, state.payload);
      if (response && response.data) {
        yield put(
          rosteringActions.exportTimesheetToPayroll.success({
            ...response.data,
            payrollType: state.payload.type,
            [`${state.payload.type}_export_status`]:
              response.data.export_status,
          })
        );
        yield put(
          generalActions.showToast({
            type: TOAST_TYPE.SUCCESS,
            title: `Timesheet ${
              response.data.export_status === PAYROLL_EXPORT_STATUS.SUCCESS
                ? "Exported Successfully!"
                : response.data.export_status ===
                  PAYROLL_EXPORT_STATUS.INITIATED
                ? "Scheduled Successfully and might be take 1-2 minutes!"
                : "Exported Successfully!"
            }`,
          })
        );
      }
    } catch (error) {
      yield put(
        rosteringActions.exportTimesheetToPayroll.error({
          error: error.message ?? error,
          payrollType: state.payload.type,
        })
      );
      yield put(
        generalActions.showToast({
          type: TOAST_TYPE.ERROR,
          title: error?.message ?? "Something Went Wrong!",
        })
      );
    }
  }

  function* getRosterInvoices(state) {
    try {
      yield put(rosteringActions.getRosterInvoices.pending);

      const response = yield call(api.getRosterInvoicing, state.payload);

      if (response) {
        yield put(rosteringActions.getRosterInvoices.success(response?.data));
      }
    } catch (error) {
      yield put(rosteringActions.getRosterInvoices.error(error));
    }
  }
  function* getPublicHolidaysOfStates(state) {

    try {
      yield put(rosteringActions.getPublicHolidaysRequest.pending);
      const response = yield call(api.getPublicHolidaysOfStatesApi,  state.data);

      if (response && response.public_holiday_list) {
        yield put(
          rosteringActions.getPublicHolidaysRequest.success(
            response.public_holiday_list
          )
        );
      }
    } catch (error) {
      yield put(rosteringActions.getPublicHolidaysRequest.error(error));
      yield put(
        generalActions.showToast({
          type: TOAST_TYPE.ERROR,
          title: error?.message ?? "Something Went Wrong!",
        })
      );
    }
  }
  function* getTimesheetOfWorkers(state) {
    try {
      yield put(rosteringActions.getTimesheetsOfWorkersAction.pending);

      const response = yield call(
        api.getTimesheetsOfWorkersService,
        state.payload
      );
      if (response && response.data) {
        yield put(
          rosteringActions.getTimesheetsOfWorkersAction.success(response.data)
        );
      }
    } catch (error) {
      yield put(
        rosteringActions.getTimesheetsOfWorkersAction.error(
          error.message ?? error
        )
      );
      yield put(
        generalActions.showToast({
          type: TOAST_TYPE.ERROR,
          title: error?.message ?? "Something Went Wrong!",
        })
      );
    }
  }

  function* fetchSingleWorkerTimesheetDetails(state) {
    try {
      yield put(
        rosteringActions.singleSelectedTimesheetOfWorkerAction.pending
      );
      const response = yield call(
        api.getTimesheetsOfSelectedWorker,
        state.payload
      );
      if (response && response.data) {
        yield put(
          rosteringActions.singleSelectedTimesheetOfWorkerAction.success(
            response.data
          )
        );
        
      }
    } catch (error) {
      yield put(
        rosteringActions.singleSelectedTimesheetOfWorkerAction.error(
          error.message ?? error
        )
      );
      yield put(
        generalActions.showToast({
          type: TOAST_TYPE.ERROR,
          title: error?.message ?? "Something Went Wrong!",
        })
      );

    }
  }

  function* fetchShiftRequestsSaga() {
    try {
      const response = yield call(api.getShiftRequests);
      const list = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
          ? response
          : [];
      yield put({
        type: ACTIONS.FETCH_SHIFT_REQUESTS.SUCCESS,
        payload: Array.isArray(list) ? list : [],
      });
    } catch (error) {
      yield put({
        type: ACTIONS.FETCH_SHIFT_REQUESTS.ERROR,
        payload: error,
      });
    }
  }

  function* updateShiftRequestStatusSaga(action) {
    const { uuid, status } = action.payload || {};
    try {
      yield call(api.updateShiftRequestStatus, uuid, status);
      yield put({ type: ACTIONS.UPDATE_SHIFT_REQUEST_STATUS.SUCCESS });
      yield put(
        generalActions.showToast({
          type: TOAST_TYPE.SUCCESS,
          title:
            status === "approved"
              ? "Shift request approved"
              : "Shift request rejected",
        })
      );
      yield put(fetchShiftRequests());
    } catch (error) {
      yield put({
        type: ACTIONS.UPDATE_SHIFT_REQUEST_STATUS.ERROR,
        payload: error,
      });
      yield put(
        generalActions.showToast({
          type: TOAST_TYPE.ERROR,
          title: error?.message ?? "Something Went Wrong!",
        })
      );
    }
  }

  yield all([
    takeEvery(
      ACTIONS.FETCH_WORKER_AND_PARTICIPANT_LIST_CALL,
      getWorkerAndParticipantList
    ),
    takeLatest(ACTIONS.FETCH_SHIFT_REQUESTS.PENDING, fetchShiftRequestsSaga),
    takeLatest(
      ACTIONS.UPDATE_SHIFT_REQUEST_STATUS.PENDING,
      updateShiftRequestStatusSaga
    ),
    takeLatest(
      ACTIONS.CREATE_OR_UPDATE_WORKER_SHIFT_CALL,
      createOrUpdateWorkerShift
    ),
    takeLatest(
      ACTIONS.WORKER_AND_PARTICIPANT_SHFIT_COPY_CALL,
      copyWorkerAndParticipantShift
    ),
    takeEvery(ACTIONS.WORKER_TIMESHEETS_CALL, getWorkerTimesheets),
    takeEvery(
      ACTIONS.PARTICIPANT_LIST_FOR_TIMESHEET_CALL,
      getParticipantListForTimesheet
    ),
    takeEvery(ACTIONS.TIMESHEET_COMMENTS_FETCH_CALL, getTimesheetComments),
    takeLatest(ACTIONS.TIMESHEET_COMMENT_CREATE_CALL, createTimesheetComment),
    takeLatest(ACTIONS.TIMESHEET_DATA_UPDATE_CALL, updateWorkerTimesheet),
    takeLatest(ACTIONS.TIMESHEET_STATUS_CHANGE_CALL, changeTimesheetStatus),
    takeEvery(
      ACTIONS.WORKER_TIMESHEET_EXPORT_LIST_CALL,
      getWorkerTimesheetExportList
    ),
    takeLatest(
      ACTIONS.SINGLE_TIMESHEET_DETAILS_CALL,
      getSingleBreakdownTimesheet
    ),
    takeLatest(
      ACTIONS.EXPORT_TIMESHEET_TO_PAYROLL_CALL,
      exportTimesheetToPayroll
    ),
    takeLatest(ACTIONS.GET_ROSTER_INVOICE_CALL, getRosterInvoices),
    takeEvery(ACTIONS.GET_PUBLIC_HOLIDAYS_LIST_CALL, getPublicHolidaysOfStates),
    takeLatest(
      ACTIONS.GET_TIMESHEET_OF_WORKERS_REQUEST,
      getTimesheetOfWorkers
    ),
    takeLatest(
      ACTIONS.SINGLE_WORKER_SELECTED_FOR_TIMESHEET_DETAIL_CALL,
      fetchSingleWorkerTimesheetDetails
    )
  ]);
}
